// Typed loader for the MCP registry.
//
// `registry.json` is generated by `rake mcp:export` in rails_app/. It joins
// three sources:
//   - controllers that opted in via `mcp_callable` / `mcp_searchable` (rails_app/lib/mcp/)
//   - the OpenAPI document (rails_app/swagger/v1/swagger.yaml)
//   - hand-authored prose in shared/api/docs/*.md
//
// Both the in-process langgraph tools and the stdio MCP server import this
// module to know which operations exist and what to say about them.

import registryJson from "./registry.json" with { type: "json" };

export type McpParameter = {
  name: string;
  in: "query" | "path" | "header" | "cookie";
  required?: boolean;
  schema?: unknown;
  description?: string;
};

export type McpRequestBody = {
  required?: boolean;
  content?: Record<string, unknown>;
};

/**
 * Per-param auto-attach declaration. Set by Rails-side
 * `mcp_callable auto_attach:`; consumed by the langgraph MCP client which
 * fills the param from current graph state before sending the HTTP
 * request, unless the agent passed an explicit value.
 *
 * `from_state` names a field on the graph state object the agent runs
 * inside (camelCase, e.g. "websiteId"). String, not symbol, because it
 * round-trips through JSON.
 */
export type McpAutoAttachSpec = {
  from_state: string;
};

export type McpOperation = {
  /** Stable identifier of the form `<resource>.<action>`, e.g. "uploads.create". */
  operation_id: string;
  method: string;
  path: string;
  summary?: string;
  parameters?: McpParameter[];
  request_body?: McpRequestBody;
  /** Hand-authored prose for the agent: when_to_use, examples, gotchas. */
  when_to_use?: string;
  /**
   * Params the agent doesn't have to pass — the langgraph MCP client
   * fills them from graph state. Agent-supplied values always win.
   * Decorated back into the response as `context_attachments` so the
   * agent learns what was attached.
   */
  auto_attach?: Record<string, McpAutoAttachSpec>;
  /**
   * User-facing phrase the chat indicator renders while this operation
   * is running. Sentence case, no trailing punctuation. e.g. "Saving
   * image to your library". Declared on the Rails side via
   * `mcp_callable label:`. Optional — frontend falls back to "Working
   * on <resource label>" when absent.
   */
  label?: string;
};

/** Where the `search` tool sends requests for one resource. */
export type McpSearchableEndpoint = { method: string; path: string };

/**
 * Schema for a resource exposed through the `search` tool. The `kind`
 * tells the agent the *shape of `q`*, not the backend mechanism:
 *
 *   - `"predicate"`: `q` is a dynamic hash of `<field>_<predicate>: value`.
 *     Fields, predicates, and sort columns are listed below.
 *   - `"schema"`:    `q` matches a fixed `query_schema` keyed by name.
 */
export type McpSearchable =
  | {
      kind: "predicate";
      endpoint: McpSearchableEndpoint;
      fields: string[];
      predicates: string[];
      sort?: string[];
      default_sort?: string;
      examples?: Array<{ q: Record<string, unknown>; purpose: string }>;
      when_to_use?: string;
      /**
       * When true, this search may answer asynchronously: the endpoint
       * returns a top-level `status` of "complete" | "partial", and
       * MCPAPIService.search polls until complete (or its budget) instead
       * of taking the first response. See docs/mcp/README.md.
       */
      pollable?: boolean;
    }
  | {
      kind: "schema";
      endpoint: McpSearchableEndpoint;
      query_schema: Record<string, unknown>;
      /**
       * Names from `query_schema` the endpoint requires. Validated
       * client-side before the request (see validateArgs) so a missing
       * required param becomes a precise error instead of a server 422.
       * Absent when the resource has no required params.
       */
      required?: string[];
      examples?: Array<{ q: Record<string, unknown>; purpose: string }>;
      when_to_use?: string;
      /** See the predicate variant — async polling opt-in. */
      pollable?: boolean;
    };

export type McpResource = {
  resource: string;
  /** One-liner from `mcp_callable(description:)` in the controller. */
  description?: string;
  /** Resource-level overview from `shared/api/docs/<resource>.md`. */
  overview?: string;
  /**
   * User-facing phrase the chat indicator uses while `search`,
   * `describe`, or `list_resources` is running against this resource.
   * Noun phrase, lowercase, no trailing punctuation. e.g. "your
   * library", "stock photos". Declared via `mcp_resource label:` on the
   * controller. Optional — frontend humanizes the registry name when
   * absent ("ad_campaigns" → "ad campaigns").
   */
  label?: string;
  /** Present when the resource is exposed through the `search` tool. */
  searchable?: McpSearchable;
  /** Present when the resource has callable mutations / side-effect ops. */
  operations?: McpOperation[];
};

export type McpRegistry = Record<string, McpResource>;

// Top-level $-prefixed keys are reserved for metadata (`$schema_version`,
// future `$generated_at`, etc.) and never iterated as resources.
const isMeta = (key: string): boolean => key.startsWith("$");

// Cast through `unknown` first because `registryJson`'s inferred shape
// includes the `$schema_version: string` meta key, which doesn't match
// the McpResource index signature. The `isMeta` filter keeps the rest of
// the API honest at runtime.
export const registry = registryJson as unknown as McpRegistry & Record<string, unknown>;

/** Registry schema version emitted by the Rails exporter. */
export function schemaVersion(): string | null {
  const v = (registryJson as Record<string, unknown>)["$schema_version"];
  return typeof v === "string" ? v : null;
}

export function listResources(): string[] {
  return Object.keys(registry).filter((k) => !isMeta(k));
}

export function describe(resource: string): McpResource | null {
  if (isMeta(resource)) return null;
  return (registry[resource] as McpResource | undefined) ?? null;
}

export function findOperation(operationId: string): McpOperation | null {
  for (const key of listResources()) {
    const resource = registry[key] as McpResource;
    const match = resource.operations?.find((op) => op.operation_id === operationId);
    if (match) return match;
  }
  return null;
}

/** Resources that opt into the `search` tool. */
export function searchableResources(): string[] {
  return listResources().filter((k) => (registry[k] as McpResource).searchable !== undefined);
}

/** Look up the searchable schema for one resource (or null if not searchable). */
export function findSearchable(resource: string): McpSearchable | null {
  if (isMeta(resource)) return null;
  return (registry[resource] as McpResource | undefined)?.searchable ?? null;
}

// ─── Labels: user-facing strings the chat indicator renders ────────────
//
// Labels are declared on the Rails side via `mcp_resource label:` and
// `mcp_callable label:`, exported into registry.json, and consumed by
// the frontend's ToolCallIndicator. The functions below give the
// consumer (typically rails_app/app/javascript) a typed read path
// instead of hand-parsing the JSON.

/** Resource-level label (e.g. "your library"). Null when unset. */
export function getResourceLabel(resource: string): string | null {
  if (isMeta(resource)) return null;
  return (registry[resource] as McpResource | undefined)?.label ?? null;
}

/** Operation-level label (e.g. "Saving image to your library"). Null when unset. */
export function getOperationLabel(operationId: string): string | null {
  const op = findOperation(operationId);
  return op?.label ?? null;
}

/**
 * Flat snapshot of every label in the registry, in the shape the
 * frontend ToolCallIndicator consumes via ToolLabelsContext. Used by
 * the launch10 chat app to wire labels in at the React tree root:
 *
 *   <ToolLabelsProvider value={getToolLabelMap()}>
 *     <Chat ... />
 *   </ToolLabelsProvider>
 *
 * Generators that add new resources or operations only need to declare
 * the label on the Rails macro — this function picks it up
 * automatically next time `rake mcp:export` runs.
 */
export interface ToolLabelMap {
  /** resource name → user-facing label (e.g. "uploads" → "your library"). */
  resources: Record<string, string>;
  /** operation_id → user-facing label (e.g. "uploads.from_url" → "Saving image to your library"). */
  operations: Record<string, string>;
}

export function getToolLabelMap(): ToolLabelMap {
  const resources: Record<string, string> = {};
  const operations: Record<string, string> = {};

  for (const name of listResources()) {
    const res = registry[name] as McpResource;
    if (res.label) resources[name] = res.label;
    for (const op of res.operations ?? []) {
      if (op.label) operations[op.operation_id] = op.label;
    }
  }

  return { resources, operations };
}
